import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaClock, FaEnvelope, FaQuestion, FaSatelliteDish } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ROLES_VEN_RADAR, puedeActuarRadar } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import {
  cargarDatosAlertasRecurrencia,
  calcularAlertasRecurrencia,
  cuentaEnContadorHeader,
  formatDiaMes,
  formatMontoCompacto,
  textoEstadoRadar,
} from "../../utils/alertasRecurrencia";
import { cerrarAlertaHastaElAnoQueViene, postergarAlerta } from "../../utils/alertasRecurrenciaDescarte";
import { monedaLocalDePais } from "../../utils/brutoCapex";
import { getConteoRedPorEstado } from "../../utils/getEdificiosOperativos";
import { fetchContactosPorLado } from "../../utils/radarContactos";
import { useCurrency } from "../../utils/useCurrency";
import { EscribirRadarModal } from "./EscribirRadarModal";
import styles from "./stylesAlertasRecurrencia.module.scss";

// "Radar Comercial" del Home (/admin) — docs/modulos/alertas-recurrencia-comercial.md. Entra en la
// misma familia que Proyección Comercial y Comisión Comercial (Ignacio, 2026-09-25) — el nombre
// completo deja claro qué barre. El ámbar es deliberado: el rojo queda reservado para los
// contadores de deuda real (Facturación, Caja); un cliente para retomar es una oportunidad, no un
// error. Vive DENTRO del <Header> vía `centerContent`, AL MEDIO (Admin/index.js arma el contenedor
// que lo centra mientras <CumpleanosBanner /> se queda pegado a la derecha).
//
// EL PANEL SE RENDERIZA EN UN PORTAL A document.body (fix 2026-09-25, ver ficha "motivo real del
// bug de apilado"): `.headerCenterZoneCentro` (stylesAdmin.module.scss) tiene `transform:
// translateX(-50%)` para centrar el botón — y CUALQUIER `transform` crea un nuevo stacking context
// Y ADEMÁS convierte a ese elemento en el containing block de sus descendientes `position:fixed`
// (spec CSS, no un detalle de este componente). Por eso NI subir el z-index NI cambiar a
// `position:fixed` sin portal alcanzaba: el panel seguía atrapado dentro de esa caja transformada,
// compitiendo solo contra sus hermanos ahí adentro, nunca contra el resto de la página. El portal
// saca el nodo del DOM entero a un hijo directo de `<body>`, fuera del subárbol transformado — ahí
// `position:fixed` sí resuelve contra el viewport real. Coordenadas medidas con
// `getBoundingClientRect()` sobre el botón disparador, recalculadas en resize/scroll mientras está
// abierto (ver `actualizarCoords`/el efecto de listeners).
//
// REGLAS §15 (Fase E, 2026-09-26, reemplazan el modelo de 5 estados de la Fase A) — "el Radar no
// desaparece solo nunca": ninguna fila sale del panel por sí sola. Solo la sacan el CHECK tildado
// (`cerrarAlertaHastaElAnoQueViene`, cierra hasta el año que viene), Postergar (`postergarAlerta`,
// 7 días) o que termine el mes del aniversario (`calcularAlertasRecurrencia` ya no la devuelve).
// `alerta.modo` decide la columna de acciones: "sobres" (el caso normal, cada lado ámbar/verde/
// interrogación) | "contacto" (todos los lados con contacto ya escritos — texto+check) | "carrera"
// (propuesta abierta este año, sin cierre — texto+check). Mismo campo consumido por el panel Y por
// `cuentaEnContadorHeader` (10.229 — nunca dos criterios).
export const AlertasRecurrenciaBanner = () => {
  const { pais, id, rol, nombre, correo } = useAuthContext();
  const puedeVer = ROLES_VEN_RADAR.includes(rol);
  const puedeActuar = puedeActuarRadar(rol, id);
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [alertas, setAlertas] = useState([]);
  const [postergarDisponible, setPostergarDisponible] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState(null); // {top, left} en coordenadas de viewport (fixed)
  const triggerRef = useRef(null); // el botón "Radar Comercial" — mide posición + outside-click
  const panelRef = useRef(null); // el panel portalado — outside-click
  // Fase B ("Escribir"): contactos por lado (clientId de la venta de referencia de cada alerta
  // VISIBLE, batcheado — nunca una query por fila), desglose de la red por estado de hoy
  // (`getConteoRedPorEstado`, junto a `getEdificiosOperativos` — 10.1/10.229) y el modal abierto
  // (o null).
  const [contactosPorCliente, setContactosPorCliente] = useState(new Map());
  const [conteoRed, setConteoRed] = useState(null);
  const [modalRadar, setModalRadar] = useState(null); // { alerta, lado } | null
  // Botón siempre visible (ajuste visual 2026-09-26): `cargado` distingue "todavía no llegó la
  // respuesta" de "llegó y no hay alertas" — antes las dos colapsaban en el mismo `alertas.length
  // === 0`, así que el botón entero desaparecía durante el fetch (hasta 8s medidos) y aparecía de
  // golpe. Se pone en `true` en las DOS ramas de abajo (`.then` Y `.catch`) — un error de carga
  // también "termina" el estado de carga, nunca lo deja colgado (10.177).
  const [cargado, setCargado] = useState(false);

  useEffect(() => {
    // Gate ANTES de cargarDatosAlertasRecurrencia (no solo en el render): un rol fuera de
    // ROLES_VEN_RADAR nunca dispara la query, aunque el componente llegue a montarse.
    if (!pais || pais === "Region" || !puedeVer) return undefined;
    let vivo = true;
    setCargado(false);
    cargarDatosAlertasRecurrencia({ pais })
      .then(({ oportunidades, clientes, descartes, mailSalienteResumen, contactoConEmailPorEmpresa }) => {
        if (!vivo) return;
        setPostergarDisponible(descartes !== null); // null = posponer_hasta todavía no existe (10.137)
        // `calcularAlertasRecurrencia` YA devuelve solo lo visible hoy (ventana encendida, no
        // postergada/cerrada — §15) — sin filtro adicional acá, a diferencia de la Fase A.
        const visibles = calcularAlertasRecurrencia({
          oportunidades,
          clientes,
          descartes,
          mailSalienteResumen,
          contactoConEmailPorEmpresa,
          pais,
        });
        setAlertas(visibles);
        setCargado(true);

        // Fase B: contactos por lado de los `clientId` de referencia de las alertas VISIBLES
        // (nunca de las ~400 empresas del país) + desglose de la red de hoy. Ninguno de los dos
        // bloquea el panel si falla — sin contactos, el modal no puede armar destinatarios; sin
        // el desglose, la frase de crecimiento se omite entera (ver plantillasRadar.js).
        const clientIds = visibles.map((a) => a.clientId).filter((cid) => cid != null);
        fetchContactosPorLado({ clientIds })
          .then((mapa) => vivo && setContactosPorCliente(mapa))
          .catch(() => {});
        getConteoRedPorEstado({ pais })
          .then((conteo) => vivo && setConteoRed(conteo))
          .catch(() => {});
      })
      .catch(() => {
        // Sin alertas visibles ante error de carga — nunca rompe el Home (10.177). `cargado` pasa
        // a `true` igual: un error también termina el estado "cargando" (si no, el botón gris
        // quedaría así para siempre en vez de simplemente desaparecer, que es el comportamiento de
        // hoy para "no hay alertas").
        if (vivo) {
          setAlertas([]);
          setCargado(true);
        }
      });
    return () => {
      vivo = false;
    };
  }, [pais, puedeVer]);

  // Panel a 1300px (ajuste visual 2026-09-26, sube desde 1000px): con datos reales de Chile y Perú
  // el motivo más largo (107 caracteres, L'ORÉAL — "...Este año ya hay una propuesta en carrera.")
  // mide ~647px con la fuente real (Nunito 400, 0.76rem, medido en Chrome headless); a 1300px la
  // columna motivo (`1fr`, absorbe todo lo que sobra de las columnas fijas) queda en ~814px —
  // sobra margen sin llegar a los 1400px de tope que pedía el prompt si hiciera falta.
  const PANEL_WIDTH = 1300;
  const MARGEN = 8;

  // Centrado respecto de la VENTANA, no del botón (ajuste visual 2026-09-26): el botón vive en
  // `.headerCenterZone`, que reparte el espacio libre entre dos hermanos de ancho asimétrico (logo+
  // saludo a la izquierda, selector de rol opcional + logout a la derecha — ver Admin/index.js) —
  // por eso el botón NUNCA queda en el centro geométrico de la ventana, y centrar el panel sobre
  // él heredaba ese corrimiento (medido: x≈290 a x≈1447 en una ventana de 2000px, no simétrico).
  // `top` sigue viniendo del botón (el panel debe abrirse debajo de él); `left` ya no.
  const actualizarCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const anchoDisponible = Math.min(PANEL_WIDTH, window.innerWidth - MARGEN * 2);
    const left = (window.innerWidth - anchoDisponible) / 2;
    setCoords({ top: rect.bottom + 6, left, width: anchoDisponible });
  }, []);

  // Mide ANTES del paint (useLayoutEffect) para que el panel no aparezca un frame en (0,0).
  useLayoutEffect(() => {
    if (abierto) actualizarCoords();
  }, [abierto, actualizarCoords]);

  // Reposiciona mientras está abierto: la ventana puede cambiar de tamaño o la página puede
  // scrollear (el botón se mueve en pantalla aunque su posición en el documento no cambie).
  // `scroll` con capture:true agarra el scroll de CUALQUIER contenedor anidado, no solo window.
  useEffect(() => {
    if (!abierto) return undefined;
    window.addEventListener("resize", actualizarCoords);
    window.addEventListener("scroll", actualizarCoords, true);
    return () => {
      window.removeEventListener("resize", actualizarCoords);
      window.removeEventListener("scroll", actualizarCoords, true);
    };
  }, [abierto, actualizarCoords]);

  // Cierre al click afuera: ahora hay que chequear DOS nodos (el botón y el panel portalado ya no
  // son el mismo subárbol del DOM desde que el panel vive en document.body).
  useEffect(() => {
    if (!abierto) return undefined;
    const handler = (e) => {
      const fueraDelBoton = triggerRef.current && !triggerRef.current.contains(e.target);
      const fueraDelPanel = panelRef.current && !panelRef.current.contains(e.target);
      if (fueraDelBoton && fueraDelPanel) setAbierto(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [abierto]);

  // Redundante con el gate del efecto de arriba (alertas nunca se puebla si !puedeVer) — explícito
  // acá también para que el botón/contador/panel nunca dependan solo de que el fetch haya sido
  // gateado correctamente (defensa en profundidad de un gate de permisos). Los roles que no ven el
  // Radar no ven NADA, en ningún estado — ni siquiera el gris de "cargando".
  if (!puedeVer) return null;

  // Botón siempre visible (ajuste visual 2026-09-26): mientras `!cargado`, gris deshabilitado, SIN
  // badge (el span existe igual, oculto por `visibility` — reserva el mismo ancho para que no haya
  // salto de layout cuando pasa al botón real). Mismo `.contenedor`/`ref`/clases base que el botón
  // de abajo — la única diferencia visual es `.botonCargando` + el badge oculto.
  if (!cargado) {
    return (
      <div className={styles.contenedor} ref={triggerRef}>
        <button type="button" className={`${styles.boton} ${styles.botonCargando}`} disabled title="Cargando radar…">
          <FaSatelliteDish className={styles.icono} aria-label="radar" />
          <span className={styles.label}>Radar Comercial</span>
          <span className={`${styles.badge} ${styles.badgeOculto}`} />
        </button>
      </div>
    );
  }

  // Ya cargó y no hay alertas — mismo comportamiento que hoy para ese caso (el botón no se
  // muestra, no solo el panel).
  if (alertas.length === 0) return null;

  const irAEmpresa = (alerta) => {
    setAbierto(false);
    navigate("/admin/ventas/crm", { state: { filtroEmpresaAlerta: alerta.empresaDisplay } });
  };

  const quitarDeLaVista = (alerta) => {
    const clave = `${alerta.empresaNorm}|${alerta.mesObjetivo}|${alerta.anoObjetivo}`;
    setAlertas((prev) => prev.filter((a) => `${a.empresaNorm}|${a.mesObjetivo}|${a.anoObjetivo}` !== clave));
  };

  const postergar = async (e, alerta) => {
    e.stopPropagation();
    quitarDeLaVista(alerta);
    try {
      await postergarAlerta({
        pais,
        empresaNorm: alerta.empresaNorm,
        mes: alerta.mesObjetivo,
        ano: alerta.anoObjetivo,
        usuarioId: id,
      });
    } catch {
      // No se pudo guardar la postergación — la alerta ya se sacó de la vista de esta sesión
      // igual; si recarga la página, vuelve a aparecer (10.177: no se finge una postergación que no
      // se guardó).
    }
  };

  // Check "ya en contacto"/"propuesta en carrera" (§15.3/§15.4/§15.9) — cierra la fila hasta el
  // año que viene (posponer_hasta = fin del mes del aniversario), para todo el equipo.
  const cerrarHastaElAnoQueViene = async (e, alerta) => {
    e.stopPropagation();
    quitarDeLaVista(alerta);
    try {
      await cerrarAlertaHastaElAnoQueViene({
        pais,
        empresaNorm: alerta.empresaNorm,
        mesObjetivo: alerta.mesObjetivo,
        anoObjetivo: alerta.anoObjetivo,
        usuarioId: id,
      });
    } catch {
      // Mismo criterio que Postergar (10.177): ya se sacó de esta sesión, vuelve si recarga.
    }
  };

  const montoTotal = alertas.reduce((suma, a) => suma + a.monto, 0);
  // Los montos ya están convertidos a la moneda LOCAL del país (10.185, `calcularAlertasRecurrencia`
  // suma en pesos/soles, nunca en USD/UF) — `formatAmount`/`formatMontoCompacto` necesitan que se
  // les diga esa moneda explícitamente (su default es CLP, no país-aware). Símbolo compacto inline:
  // no hay todavía un mapa moneda→símbolo centralizado (10.27) para un ternario de 2 valores.
  const monedaLocal = monedaLocalDePais(pais);
  const simboloMonto = monedaLocal === "PEN" ? "S/" : "$";
  // Badge del botón (§15.8): SOLO filas que necesitan acción — puede ser MENOR que `alertas.length`
  // (el heading de abajo, "Encontramos N clientes", sigue mostrando el total de filas visibles;
  // son dos números con propósitos distintos a propósito, ver `cuentaEnContadorHeader`).
  const contadorHeader = alertas.filter(cuentaEnContadorHeader).length;

  // Sobre de un lado (§15.2/§15.7) — ámbar (clickeable, abre el modal) | verde (deshabilitado,
  // tooltip con la fecha) | interrogación (deshabilitado, sin contacto con email). Nunca gris.
  const renderSobre = (lado, alerta) => {
    const hay = lado === "cliente" ? alerta.hayCliente : alerta.hayAgencia;
    const escrito = lado === "cliente" ? alerta.escritoCliente : alerta.escritoAgencia;
    const fecha = lado === "cliente" ? alerta.fechaEscritoCliente : alerta.fechaEscritoAgencia;
    const letra = lado === "cliente" ? "C" : "A";
    const nombreLado = lado === "cliente" ? "cliente" : "agencia";

    if (!hay) {
      // `<FaQuestion />` (SVG), nunca un carácter "?" de texto — un glifo de texto mide distinto
      // que un ícono SVG del mismo `font-size` (medido en Chrome headless: 33.2px de alto de fila
      // contra 33.1875px del resto) — el mismo TIPO de ícono que `<FaEnvelope />` es lo que
      // garantiza el alto de fila idéntico entre los 3 estados del sobre.
      return (
        <span
          className={`${styles.accionBoton} ${styles.accionBotonInterrogacion}`}
          title={`Sin contactos de ${nombreLado} con email`}
        >
          <FaQuestion />
          <span className={styles.sobreLetra}>{letra}</span>
        </span>
      );
    }
    if (escrito) {
      return (
        <span
          className={`${styles.accionBoton} ${styles.accionBotonVerde}`}
          title={`Le escribiste el ${formatDiaMes(fecha)}`}
        >
          <FaEnvelope />
          <span className={styles.sobreLetra}>{letra}</span>
        </span>
      );
    }
    return (
      <button
        type="button"
        className={`${styles.accionBoton} ${styles.accionBotonAmbar}`}
        onClick={(e) => {
          e.stopPropagation();
          setModalRadar({ alerta, lado });
        }}
        title={`Escribir a ${lado === "cliente" ? "Cliente" : "Agencia"}`}
      >
        <FaEnvelope />
        <span className={styles.sobreLetra}>{letra}</span>
      </button>
    );
  };

  const panel = abierto && coords && (
    <div className={styles.panel} ref={panelRef} style={{ top: coords.top, left: coords.left, width: coords.width }}>
      <div className={styles.encabezado}>
        <div className={styles.encabezadoTitulo}>
          Encontramos {alertas.length} {alertas.length === 1 ? "cliente" : "clientes"}.{" "}
          {formatMontoCompacto(montoTotal, simboloMonto)} entre todos.
        </div>
      </div>
      <div className={styles.lista}>
        {alertas.map((alerta) => {
          const clave = `${alerta.empresaNorm}-${alerta.mesObjetivo}-${alerta.anoObjetivo}`;
          return (
            <div key={clave} className={styles.fila} onClick={() => irAEmpresa(alerta)}>
              <span className={`${styles.punto} ${alerta.tipo === "compro" ? styles.puntoVerde : styles.puntoGris}`} />
              <span className={styles.colEmpresa} title={alerta.empresaDisplay}>
                {alerta.empresaDisplay}
              </span>
              <span className={styles.colMotivo} title={alerta.motivo}>
                {alerta.motivo}
              </span>
              <span className={styles.colMonto}>{formatAmount(alerta.monto, monedaLocal)}</span>
              {/* Separador monto/acciones (ajuste visual 2026-09-26) — sexta columna de grid de
                  `.fila`, EN TODAS las filas sin excepción (sobres o check), a diferencia de las
                  barras `.divisorAcciones` de más abajo que solo viven dentro del grupo de sobres. */}
              <span className={styles.divisorMontoAcciones} />
              {/* Columna de acciones — ancho fijo (95px, grid-template-columns de `.fila`), igual
                  para quien actúa y quien solo ve (`puedeActuarRadar`, permisos.js): el importe
                  nunca se mueve. modo="sobres": 3 lugares reservados (sobre C, sobre A, reloj) con
                  4 barras finas. modo="contacto"/"carrera": un solo botón check, reemplaza los 3
                  lugares (el texto va en `colMotivo`, ya viene armado en `alerta.motivo`). Quien
                  solo ve, el estado en texto (`textoEstadoRadar`) centrado en el mismo slot. */}
              <span className={styles.acciones}>
                {!puedeActuar ? (
                  <span className={styles.estadoTexto} title={textoEstadoRadar(alerta)}>
                    {textoEstadoRadar(alerta)}
                  </span>
                ) : alerta.modo === "sobres" ? (
                  <>
                    <span className={styles.divisorAcciones} />
                    <span className={styles.accionSlot}>{renderSobre("cliente", alerta)}</span>
                    <span className={styles.divisorAcciones} />
                    <span className={styles.accionSlot}>{renderSobre("agencia", alerta)}</span>
                    <span className={styles.divisorAcciones} />
                    <span className={styles.accionSlot}>
                      {postergarDisponible && (
                        <button
                          type="button"
                          className={`${styles.accionBoton} ${styles.accionBotonNeutro}`}
                          onClick={(e) => postergar(e, alerta)}
                          title="Postergar 7 días"
                        >
                          <FaClock />
                        </button>
                      )}
                    </span>
                    <span className={styles.divisorAcciones} />
                  </>
                ) : (
                  <button
                    type="button"
                    className={styles.botonCheck}
                    onClick={(e) => cerrarHastaElAnoQueViene(e, alerta)}
                    title="Cerrar hasta el año que viene"
                  >
                    <FaCheck />
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
      <div className={styles.pie}>
        <span className={styles.pieLlave}>
          <span className={`${styles.pieSobre} ${styles.accionBotonAmbar}`}>
            <FaEnvelope />
          </span>{" "}
          Falta escribir
        </span>
        <span className={styles.pieLlave}>
          <span className={`${styles.pieSobre} ${styles.accionBotonVerde}`}>
            <FaEnvelope />
          </span>{" "}
          Ya escribiste
        </span>
        <span className={styles.pieLlave}>
          <span className={`${styles.pieSobre} ${styles.accionBotonInterrogacion}`}>?</span> Sin contacto con email
        </span>
        <span className={styles.pieDescarte}>
          Postergar lo oculta 7 días · El check lo cierra hasta el año que viene
        </span>
      </div>
    </div>
  );

  return (
    <div className={styles.contenedor} ref={triggerRef}>
      <button type="button" className={styles.boton} onClick={() => setAbierto((v) => !v)}>
        <FaSatelliteDish className={styles.icono} aria-label="radar" />
        <span className={styles.label}>Radar Comercial</span>
        <span className={styles.badge}>{contadorHeader}</span>
      </button>
      {panel && createPortal(panel, document.body)}
      {modalRadar &&
        (() => {
          const contactosCliente = contactosPorCliente.get(modalRadar.alerta.clientId) || {
            cliente: [],
            agencia: [],
          };
          return (
            <EscribirRadarModal
              isOpen
              onClose={() => setModalRadar(null)}
              lado={modalRadar.lado}
              alerta={modalRadar.alerta}
              contactos={contactosCliente[modalRadar.lado]}
              conteoRed={conteoRed}
              hayAgenciaConEmail={modalRadar.alerta.hayAgencia}
              pais={pais}
              usuarioId={id}
              emailVendedor={correo}
              nombreVendedor={nombre}
            />
          );
        })()}
    </div>
  );
};
